import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { makeBranchApi } from "../../api/branchApi";
import {
  PageHeader, Btn, EmptyState, LoadingSpinner,
  ConfirmModal, FormField, selectStyle,
} from "../../components/ui";
import Modal from "../../components/ui/Modal";
import toast from "react-hot-toast";

const CLIP_TYPES = [
  { value: "chime",        label: "Attention Chime",    desc: "Plays before every announcement to get attention",                   example: "ding.mp3" },
  { value: "station_name", label: "Station Name",       desc: "Spoken name of a station — one clip per station",                    example: '"Customer Care Counter"' },
  { value: "number_digit", label: "Number Digit (0–9)", desc: "One clip per digit. System combines them to say ticket numbers",     example: '"Zero", "One", … "Nine"' },
  { value: "connector",    label: "Connector Phrase",   desc: 'Phrase between number and station: "please proceed to"',             example: '"please proceed to"' },
  { value: "welcome",      label: "Welcome Message",    desc: "Plays when kiosk starts up",                                         example: '"Welcome to City Hospital"' },
  { value: "complete",     label: "Completion Message", desc: "Plays when a patient's journey is fully complete",                   example: '"Thank you, your visit is complete"' },
];

const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "tw", label: "Twi" },
];

export default function BranchAudioPage() {
  const { branchId } = useParams();
  const api = makeBranchApi(branchId);

  const [assets,  setAssets]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadModal,   setUploadModal]   = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [playing, setPlaying] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.listAudio()
      .then(r => setAssets(r.data?.results || r.data || []))
      .catch(() => toast.error("Failed to load audio assets."))
      .finally(() => setLoading(false));
  }, [branchId]);

  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    setSaving(true);
    try {
      await api.deleteAudio(deleteConfirm.id);
      toast.success("Audio clip deleted.");
      setDeleteConfirm(null); load();
    } catch { toast.error("Failed to delete."); }
    finally  { setSaving(false); }
  };

  const handlePlay = (asset) => {
    if (playing === asset.id) { setPlaying(null); return; }
    setPlaying(asset.id);
    const audio = new Audio(asset.file_url);
    audio.play().catch(() => toast.error("Could not play audio."));
    audio.onended = () => setPlaying(null);
  };

  const assetsForType = (type) => assets.filter(a => a.clip_type === type);

  return (
    <div style={styles.root}>
      <PageHeader
        title="Audio & Sounds"
        subtitle="Upload audio clips for queue announcements. The display board uses these to announce ticket numbers aloud."
        action={<Btn onClick={() => setUploadModal(true)}>+ Upload Clip</Btn>}
      />

      <div style={styles.howItWorks}>
        <span style={{ fontSize: "18px" }}>🔊</span>
        <div>
          <div style={{ fontSize: "13px", fontWeight: "600", color: "#60A5FA", marginBottom: "4px" }}>How announcement audio works</div>
          <div style={{ fontSize: "12px", color: "var(--text-secondary)" }}>
            When a ticket is called, the board plays: <code style={{ color: "#A78BFA" }}>[Chime] → [Ticket number digits] → [Connector phrase] → [Station name]</code>.
            If any clip is missing, the board falls back to browser text-to-speech automatically.
          </div>
        </div>
      </div>

      {loading ? <LoadingSpinner /> : (
        <div style={styles.clipList}>
          {CLIP_TYPES.map(ct => {
            const typeAssets = assetsForType(ct.value);
            return (
              <div key={ct.value} style={styles.clipSection}>
                <div style={styles.clipHeader}>
                  <div style={{ flex: 1 }}>
                    <div style={styles.clipTitle}>{ct.label}</div>
                    <div style={styles.clipDesc}>{ct.desc}</div>
                    <div style={styles.clipExample}>Example: {ct.example}</div>
                  </div>
                  <Btn size="sm" variant="ghost" onClick={() => setUploadModal({ clip_type: ct.value })}>+ Upload</Btn>
                </div>
                {typeAssets.length === 0 ? (
                  <div style={styles.noClip}>No clips uploaded — system will use text-to-speech fallback.</div>
                ) : typeAssets.map(asset => (
                  <div key={asset.id} style={styles.assetRow}>
                    <button onClick={() => handlePlay(asset)} style={styles.playBtn}>
                      {playing === asset.id ? "⏸" : "▶"}
                    </button>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: "13px", color: "var(--text-primary)", fontWeight: "500" }}>{asset.label || asset.filename}</div>
                      <div style={{ fontSize: "11px", color: "var(--text-muted)" }}>
                        {LANGUAGES.find(l => l.value === asset.language)?.label || asset.language}
                        {asset.station_name && ` · ${asset.station_name}`}
                      </div>
                    </div>
                    <Btn size="sm" variant="danger" onClick={() => setDeleteConfirm(asset)}>Delete</Btn>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      )}

      <UploadModal
        open={!!uploadModal}
        defaultType={uploadModal?.clip_type || ""}
        onClose={() => setUploadModal(false)}
        onDone={() => { setUploadModal(false); load(); }}
        api={api}
      />

      <ConfirmModal
        open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDelete} loading={saving}
        title="Delete Audio Clip?"
        message={`"${deleteConfirm?.label || deleteConfirm?.filename}" will be removed.`}
        confirmLabel="Delete" variant="danger"
      />
    </div>
  );
}

function UploadModal({ open, defaultType, onClose, onDone, api }) {
  const [form, setForm] = useState({ clip_type: "", language: "en", label: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => { if (open) { setForm({ clip_type: defaultType || "", language: "en", label: "" }); setFile(null); } }, [open, defaultType]);

  const submit = async () => {
    if (!file || !form.clip_type) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("clip_type", form.clip_type);
      fd.append("language", form.language);
      fd.append("label", form.label || file.name);
      await api.uploadAudio(fd);
      toast.success("Audio clip uploaded.");
      onDone();
    } catch { toast.error("Upload failed."); }
    finally  { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Upload Audio Clip" width="440px">
      <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
        <FormField label="Clip Type" required>
          <select style={selectStyle} value={form.clip_type} onChange={e => set("clip_type", e.target.value)}>
            <option value="">Select type…</option>
            {CLIP_TYPES.map(ct => <option key={ct.value} value={ct.value}>{ct.label}</option>)}
          </select>
        </FormField>
        <FormField label="Language">
          <select style={selectStyle} value={form.language} onChange={e => set("language", e.target.value)}>
            {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        </FormField>
        <FormField label="Label (optional)">
          <input
            style={{ background: "var(--input-bg)", border: "1px solid var(--input-border)", borderRadius: "8px", padding: "9px 14px", color: "var(--text-primary)", fontSize: "14px", outline: "none", width: "100%", fontFamily: "inherit", boxSizing: "border-box" }}
            value={form.label}
            onChange={e => set("label", e.target.value)}
            placeholder="e.g. Customer Care Counter"
          />
        </FormField>
        <FormField label="Audio File" required>
          <input type="file" accept="audio/*" onChange={e => setFile(e.target.files[0])}
            style={{ color: "var(--text-primary)", fontSize: "13px" }} />
        </FormField>
        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={submit} disabled={saving || !file || !form.clip_type}>{saving ? "Uploading…" : "Upload"}</Btn>
        </div>
      </div>
    </Modal>
  );
}

const styles = {
  root:         { padding: "32px", fontFamily: "'DM Sans','Segoe UI',sans-serif", maxWidth: "860px" },
  howItWorks:   { display: "flex", gap: "14px", alignItems: "flex-start", background: "rgba(37,99,235,0.06)", border: "1px solid var(--surface-border)", borderRadius: "12px", padding: "14px 18px", marginBottom: "24px" },
  clipList:     { display: "flex", flexDirection: "column", gap: "12px" },
  clipSection:  { background: "var(--surface-bg)", border: "1px solid var(--surface-border)", borderRadius: "12px", overflow: "hidden" },
  clipHeader:   { display: "flex", alignItems: "flex-start", gap: "12px", padding: "16px 20px", borderBottom: "1px solid rgba(37,99,235,0.08)" },
  clipTitle:    { fontSize: "14px", fontWeight: "700", color: "var(--text-primary)", marginBottom: "2px" },
  clipDesc:     { fontSize: "12px", color: "var(--text-secondary)" },
  clipExample:  { fontSize: "11px", color: "var(--text-faint)", marginTop: "2px", fontStyle: "italic" },
  noClip:       { padding: "12px 20px", fontSize: "12px", color: "var(--text-faint)", fontStyle: "italic" },
  assetRow:     { display: "flex", alignItems: "center", gap: "12px", padding: "10px 20px", borderTop: "1px solid rgba(37,99,235,0.06)" },
  playBtn:      { background: "var(--surface-border)", border: "1px solid rgba(37,99,235,0.2)", borderRadius: "6px", color: "#60A5FA", width: "32px", height: "32px", cursor: "pointer", fontSize: "13px", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 },
};
